import React, { useState, useEffect } from "react";
import { Enrollment } from "../../../../../service/enrollment";
import { updateStudentLoginDetails } from "../../../../../service/student";

interface EditLoginModalProps {
  student: Enrollment | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditLoginModal: React.FC<EditLoginModalProps> = ({
  student,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  useEffect(() => {
    if (student) {
      setFormData({
        email: student.user?.email || "",
        password: "",
      });
    }
    setError("");
    setSuccess("");
  }, [student]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!student) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const submitData = {
        email: formData.email,
        password: formData.password,
      };

      if (formData.password.trim()) {
        submitData.password = formData.password;
      }

      const response = await updateStudentLoginDetails(student._id, submitData);

      if (response.success) {
        setSuccess("Login details updated successfully!");
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to update login details");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ email: "", password: "" });
    setError("");
    setSuccess("");
    setIsPasswordVisible(false);
    onClose();
  };

  if (!student) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">
              Edit Login Details - {student?.user?.firstName}{" "}
              {student?.user?.lastName}
            </h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              onClick={handleClose}
              disabled={loading}
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <div className="mb-3">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">New Password</label>
                <div className="pass-group">
                  <input
                    name="password"
                    type={isPasswordVisible ? "text" : "password"}
                    className="pass-input form-control"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Leave blank to keep current password"
                    disabled={loading}
                  />
                  <span
                    className={`ti toggle-password ${
                      isPasswordVisible ? "ti-eye" : "ti-eye-off"
                    }`}
                    onClick={togglePasswordVisibility}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditLoginModal;
